/**
 * 顾问陪跑 API client —— 客户档案 / 顾问专属订单 / 转交。
 *
 * 金额字段一律为「分」，展示层负责 /100。
 * 名下范围由后端强制：销售调这些接口只会拿到自己名下的客户，别人的一律 404。
 */
import { apiClient } from './client';

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * 一本书的讲述人背景（草案 §9）。全部可选，存成一个 JSON 对象，挂在订单（一单一书）上；
 * 讲述人称呼与关系是订单顶层字段（narratorName / narratorRelation），不在这里。
 */
export interface AdvisorBackground {
  birthYear?: string;
  birthPlace?: string;
  residence?: string;
  gender?: string;
  lifeSkeleton?: string;
  keyPeople?: string;
  highlights?: string;
  eraMarks?: string;
  /** 禁区：不想提的话题。必填（可以填「无」） */
  taboos?: string;
  writtenFor?: string;
  /** 期望交付日期 YYYY-MM-DD */
  deadline?: string;
  copies?: number;
  /** 操作方式：elder_self 老人自己用手机 / child_assist 子女代操作 / together 一起 */
  operationMode?: 'elder_self' | 'child_assist' | 'together';
  /** 听力、方言、识字等 */
  conditions?: string;
  materials?: string;
  stylePreference?: string;
}

export interface PartnerOverlapDto {
  partnerId: string;
  detectedAt: number | null;
}

export interface BookNarratorDto {
  advisorOrderId: string;
  narratorName: string | null;
  narratorRelation: string | null;
}

/** 一本书（订单）现在走到哪一步（销售工作台）；规则在 admin-service 的 advisor-stage.ts */
export type BookStage =
  | 'awaiting_payment'
  | 'expired'
  | 'narrating'
  | 'awaiting_confirm'
  | 'finalized'
  | 'print_ordered'
  | 'shipped'
  | 'refunded';

/** 在等销售动手的几类事（顺序即紧急程度） */
export type Attention =
  | 'pay_expiring'
  | 'confirm_pending'
  | 'print_pending'
  | 'ship_pending'
  | 'idle'
  | 'pay_expired';

export interface BookOverviewDto {
  advisorOrderId: string;
  workId: string | null;
  narratorName: string | null;
  narratorRelation: string | null;
  stage: BookStage;
  expiresAt: number;
  paidAt: number | null;
  chapterCount: number;
  /** 已聊 / 总题数（近似，以详情页「问题清单与进度」为准）；没开书为 null */
  progress: { discussed: number; total: number } | null;
  lastNarratedAt: number | null;
  /** 讲述中的书已经几天没讲；其他阶段为 null */
  idleDays: number | null;
  attention: Attention[];
}

export interface AdvisorCustomerSummaryDto {
  customerId: string;
  advisorAdminId: string;
  advisorNickname: string | null;
  displayName: string;
  contactPhone: string | null;
  /** 名下各本书的讲述人（每本书各写谁；已取消的单不算，没填讲述人的单不列） */
  narrators: BookNarratorDto[];
  linkedUser: boolean;
  orderCount: number;
  paidCount: number;
  awaitingCount: number;
  /** 各本书里最近一次讲述；一本都没讲过为 null */
  lastActiveAt: number | null;
  createdAt: number;
  updatedAt: number;
  /** 仅超管可见；销售恒为 null */
  partnerOverlap: PartnerOverlapDto | null;
  /** 名下每本书（未取消的订单）的阶段 / 进度 / 几天没讲 */
  books: BookOverviewDto[];
  /** 这位客户有哪些事在等销售动手，按紧急程度排好 */
  attention: Attention[];
}

export type AdvisorOrderView = 'awaiting_payment' | 'expired' | 'paid' | 'cancelled' | 'refunded';

export interface AdvisorOrderDto {
  advisorOrderId: string;
  amountFen: number;
  productTitle: string;
  includedCopyCount: number;
  status: string;
  view: AdvisorOrderView;
  expiresAt: number;
  paidAt: number | null;
  paidUserId: string | null;
  workId: string | null;
  shippingAddress: Record<string, string> | null;
  agreementVersion: string | null;
  /** 这本书的讲述人与背景（一单一书，爸妈各开一单各一份） */
  narratorName: string | null;
  narratorRelation: string | null;
  background: AdvisorBackground | null;
  cancelledAt: number | null;
  refundedAt: number | null;
  createdAt: number;
  /** 仅超管可见 */
  commissionHold: { status: string; fen: number | null; partnerId: string | null } | null;
}

export interface AdvisorAssignmentLogDto {
  logId: string;
  fromAdminId: string;
  fromNickname: string | null;
  toAdminId: string;
  toNickname: string | null;
  actorNickname: string | null;
  reason: string | null;
  createdAt: number;
}

export interface AdvisorCustomerDetailDto extends AdvisorCustomerSummaryDto {
  wecomExternalUserid: string | null;
  userId: string | null;
  orders: AdvisorOrderDto[];
  assignmentLogs: AdvisorAssignmentLogDto[];
}

export interface AdvisorCustomerInput {
  displayName?: string;
  contactPhone?: string | null;
  /** 仅超管代建档时有效 */
  advisorAdminId?: string;
}

/** 一本书（订单）的讲述人与背景；未出现的键 = 不改，null = 清空 */
export interface AdvisorBookProfileInput {
  narratorName?: string | null;
  narratorRelation?: string | null;
  background?: AdvisorBackground | null;
}

export interface AdvisorCustomerFilter {
  keyword?: string;
  overlapOnly?: boolean;
  /** 只看有书处于这个阶段的客户 */
  stage?: BookStage;
  /** 只看有这类待跟进事项的客户 */
  attention?: Attention;
  /** 只看有讲述中的书已经 N 天没讲的客户 */
  idleDays?: number;
  /** 仅超管：只看某位销售名下的客户 */
  advisorAdminId?: string;
  page?: number;
  pageSize?: number;
}

export async function listAdvisorCustomers(filter: AdvisorCustomerFilter) {
  const params: Record<string, string> = {};
  if (filter.keyword) params.keyword = filter.keyword;
  if (filter.overlapOnly) params.overlapOnly = '1';
  if (filter.stage) params.stage = filter.stage;
  if (filter.attention) params.attention = filter.attention;
  if (filter.idleDays) params.idleDays = String(filter.idleDays);
  if (filter.advisorAdminId) params.advisorAdminId = filter.advisorAdminId;
  if (filter.page) params.page = String(filter.page);
  if (filter.pageSize) params.pageSize = String(filter.pageSize);
  const resp = await apiClient.get<
    ServiceResponse<{
      items: AdvisorCustomerSummaryDto[];
      total: number;
      page: number;
      pageSize: number;
      /** 带阶段 / 待跟进筛选时最多扫描 1000 位客户；超过时只筛了其中最近更新的那些 */
      truncated: boolean;
    }>
  >('/advisor/customers', { params });
  return resp.data.data;
}

export async function getAdvisorCustomer(customerId: string) {
  const resp = await apiClient.get<ServiceResponse<AdvisorCustomerDetailDto>>(
    `/advisor/customers/${encodeURIComponent(customerId)}`,
  );
  return resp.data.data;
}

export async function createAdvisorCustomer(input: AdvisorCustomerInput) {
  const resp = await apiClient.post<ServiceResponse<{ customerId: string }>>('/advisor/customers', input);
  return resp.data.data;
}

export async function updateAdvisorCustomer(customerId: string, input: AdvisorCustomerInput) {
  const resp = await apiClient.patch<ServiceResponse<{ customerId: string }>>(
    `/advisor/customers/${encodeURIComponent(customerId)}`,
    input,
  );
  return resp.data.data;
}

export async function createAdvisorOrder(
  customerId: string,
  input: { amountFen: number; productTitle?: string; expiresInDays?: number } & AdvisorBookProfileInput,
) {
  const resp = await apiClient.post<ServiceResponse<{ advisorOrderId: string }>>(
    `/advisor/customers/${encodeURIComponent(customerId)}/orders`,
    input,
  );
  return resp.data.data;
}

/** 改这本书的讲述人 / 背景（订单任何状态都能改：付款后才陆续录背景） */
export async function updateAdvisorOrderProfile(advisorOrderId: string, input: AdvisorBookProfileInput) {
  const resp = await apiClient.patch<ServiceResponse<{ advisorOrderId: string }>>(
    `/advisor/orders/${encodeURIComponent(advisorOrderId)}/profile`,
    input,
  );
  return resp.data.data;
}

export async function cancelAdvisorOrder(advisorOrderId: string) {
  const resp = await apiClient.post<ServiceResponse<{ advisorOrderId: string }>>(
    `/advisor/orders/${encodeURIComponent(advisorOrderId)}/cancel`,
    {},
  );
  return resp.data.data;
}

export async function transferAdvisorCustomers(input: { customerIds: string[]; toAdminId: string; reason?: string }) {
  const resp = await apiClient.post<ServiceResponse<{ transferred: string[]; skipped: string[] }>>(
    '/advisor/customers/transfer',
    input,
  );
  return resp.data.data;
}

export async function listAdvisorSales() {
  const resp = await apiClient.get<ServiceResponse<Array<{ id: string; nickname: string }>>>('/advisor/sales');
  return resp.data.data;
}

// ── 销售工作台：今日待跟进（A-1）──────────────────────────────────────────────

export interface WorkbenchItemDto {
  customerId: string;
  displayName: string;
  /** 这本书的讲述人，如「王秀英（母亲）」；没填为空串 */
  bookLabel: string;
  /** 归属销售昵称——仅超管下发 */
  advisorNickname: string | null;
  stage: BookStage;
  attention: Attention;
  idleDays: number | null;
  expiresAt: number;
  paidAt: number | null;
  lastNarratedAt: number | null;
}

export interface DisabledOwnerDto {
  advisorAdminId: string;
  nickname: string | null;
  /** disabled = 账号已停用；not_sales = 账号不再是销售；missing = 账号已被删除 */
  reason: 'disabled' | 'not_sales' | 'missing';
  customerCount: number;
}

export interface WorkbenchDto {
  /** 每类待跟进的书数（一位客户两本书都在等，算两次） */
  counts: Record<Attention, number>;
  /** 每类最紧急的几条 */
  items: Record<Attention, WorkbenchItemDto[]>;
  /** 仅超管：名下还有客户、但归属人账号已停用 / 不再是销售 / 已删除——需要转交 */
  unassignedOwners: DisabledOwnerDto[] | null;
}

export async function getAdvisorWorkbench() {
  const resp = await apiClient.get<ServiceResponse<WorkbenchDto>>('/advisor/workbench');
  return resp.data.data;
}

// ── 销售管理（A-4，仅超管）────────────────────────────────────────────────────

export interface SalesOverviewRowDto {
  id: string;
  nickname: string;
  role: string;
  status: string;
  /** 是否已绑企业微信成员（侧边栏靠它识别销售） */
  wecomBound: boolean;
  customerCount: number;
  stageCounts: Partial<Record<BookStage, number>>;
  attentionCounts: Partial<Record<Attention, number>>;
  /** 本月（UTC+8）已付款成交单数与金额（分），按「当前归属销售」算；已退款的不算 */
  monthPaidCount: number;
  monthPaidFen: number;
}

export async function getAdvisorSalesOverview() {
  const resp = await apiClient.get<ServiceResponse<{ rows: SalesOverviewRowDto[]; monthLabel: string }>>(
    '/advisor/sales/overview',
  );
  return resp.data.data;
}

/** 离职交接：把这位销售名下的全部客户转给另一位 */
export async function transferAllFromSales(fromAdminId: string, input: { toAdminId: string; reason?: string }) {
  const resp = await apiClient.post<ServiceResponse<{ transferred: string[]; skipped: string[] }>>(
    `/advisor/sales/${encodeURIComponent(fromAdminId)}/transfer-all`,
    input,
  );
  return resp.data.data;
}

// ── 团队名册（运营主管 advisor:team_view）：不含联系方式与内容 ─────────────────────────

/**
 * 名册的一行：只有这些字段（设计 I5）——没有手机号、企微、讲述人、合伙人交叉、订单金额、背景。
 * 服务端按白名单投影，这里的类型也不要加这些字段。
 */
export interface TeamCustomerDto {
  customerId: string;
  displayName: string;
  advisorAdminId: string;
  advisorNickname: string | null;
  /** 名下各阶段的书数 */
  stageCounts: Partial<Record<BookStage, number>>;
  paidCount: number;
  awaitingCount: number;
  createdAt: number;
  /** 各本书里最近一次讲述；一本都没讲过为 null */
  lastActiveAt: number | null;
}

export interface TeamRosterDto {
  items: TeamCustomerDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** 某一位销售名下的客户名册（必须指定销售，没有「全部客户」） */
export async function listAdvisorTeamCustomers(params: { salesId: string; page: number; pageSize: number }) {
  const resp = await apiClient.get<ServiceResponse<TeamRosterDto>>('/advisor/team/customers', { params });
  return resp.data.data;
}

// ── 顾问设置：开单金额区间（超管可调）────────────────────────────────────────

export interface AdvisorOrderAmountRangeDto {
  minFen: number;
  maxFen: number;
  defaults: { minFen: number; maxFen: number };
  /** 后端硬边界：下限 = 自助版公开价（¥699），上限 ¥50000；设置不可越过 */
  bounds: { floorFen: number; ceilFen: number };
  /** true = 还没人调过 */
  usingDefaults: boolean;
  updatedBy: string | null;
  updatedAt: number | null;
}

export async function getAdvisorOrderAmountRange() {
  const resp = await apiClient.get<ServiceResponse<AdvisorOrderAmountRangeDto>>('/advisor/settings/order-amount-range');
  return resp.data.data;
}

export async function updateAdvisorOrderAmountRange(input: { minFen: number; maxFen: number }) {
  const resp = await apiClient.patch<
    ServiceResponse<AdvisorOrderAmountRangeDto & { before: { minFen: number; maxFen: number } }>
  >('/advisor/settings/order-amount-range', input);
  return resp.data.data;
}

// ── 定稿与印刷（PR⑤）──────────────────────────────────────────────────────────

/** 与印刷单 receiver_* 同字段；小程序付款时填写，存在书上 */
export interface ShippingAddressDto {
  receiverName: string;
  receiverPhone: string;
  receiverProvince: string;
  receiverCity: string;
  receiverDistrict: string;
  receiverAddress: string;
}

export type FinalizeRequestStatus = 'pending' | 'confirmed' | 'invalidated' | 'cancelled';

export interface FinalizeRequestBrief {
  requestId: string;
  status: FinalizeRequestStatus;
  createdAt: number;
  requestedBy: string;
  confirmedAt: number | null;
  confirmedUserId: string | null;
  invalidatedAt: number | null;
  cancelledAt: number | null;
  addressUpdatedAt: number | null;
}

export interface AdvisorPrintOrderDto {
  orderId: string;
  status: string;
  paymentStatus: string;
  logisticsStatus: string;
  quantity: number;
  totalPriceFen: number;
  receiver: { [K in keyof ShippingAddressDto]: string | null };
  logisticsCompany: string | null;
  trackingNumber: string | null;
  shippedAt: number | null;
  createdAt: number;
}

export interface WorkFulfillmentDto {
  workId: string;
  title: string;
  workStatus: string;
  lifecycleStatus: string;
  chapterCount: number;
  finalized: boolean;
  /** 读时已重算内容指纹：发起后改过内容会显示 invalidated */
  latestRequest: FinalizeRequestBrief | null;
  /** 有待确认请求时发给客户的小程序路径 */
  confirmPagePath: string | null;
  shippingAddress: ShippingAddressDto | null;
  printCreditCount: number;
  printOrders: AdvisorPrintOrderDto[];
}

const w = (workId: string) => `/advisor/works/${encodeURIComponent(workId)}`;

export async function getWorkFulfillment(workId: string) {
  const resp = await apiClient.get<ServiceResponse<WorkFulfillmentDto>>(`${w(workId)}/fulfillment`);
  return resp.data.data;
}

export async function startFinalizeRequest(workId: string) {
  const resp = await apiClient.post<ServiceResponse<FinalizeRequestBrief>>(`${w(workId)}/finalize-requests`, {});
  return resp.data.data;
}

export async function cancelFinalizeRequest(requestId: string) {
  const resp = await apiClient.post<ServiceResponse<FinalizeRequestBrief>>(
    `/advisor/finalize-requests/${encodeURIComponent(requestId)}/cancel`,
    {},
  );
  return resp.data.data;
}

export async function updateWorkShippingAddress(workId: string, shippingAddress: ShippingAddressDto) {
  const resp = await apiClient.patch<ServiceResponse<{ workId: string }>>(`${w(workId)}/shipping-address`, {
    shippingAddress,
  });
  return resp.data.data;
}

export async function createAdvisorPrintOrder(workId: string) {
  const resp = await apiClient.post<ServiceResponse<{ orderId: string; status: string }>>(`${w(workId)}/print-order`, {});
  return resp.data.data;
}

export async function shipAdvisorPrintOrder(orderId: string, input: { company: string; trackingNumber: string }) {
  const resp = await apiClient.post<ServiceResponse<{ orderId: string }>>(
    `/advisor/print-orders/${encodeURIComponent(orderId)}/ship`,
    input,
  );
  return resp.data.data;
}

export async function updateAdvisorPrintOrderAddress(orderId: string, input: Partial<ShippingAddressDto>) {
  const resp = await apiClient.patch<ServiceResponse<{ orderId: string }>>(
    `/advisor/print-orders/${encodeURIComponent(orderId)}/address`,
    input,
  );
  return resp.data.data;
}

// ── 问题清单与进度 / 代改大纲（PR④）────────────────────────────────────────

export interface AdvisorProgressQuestion {
  questionId: string;
  text: string;
  discussed: boolean;
  /** 用户（或销售代）自己加的题 */
  userAdded: boolean;
  photoCount: number;
  /** 叙叙问到这道题的时间；没问过为 null */
  askedAt: number | null;
}

export interface AdvisorProgressTopic {
  topicId: string;
  title: string;
  questionCount: number;
  discussedCount: number;
  photoCount: number;
  /** 聊到这个话题的讲述通数 */
  narrationCount: number;
  lastNarratedAt: number | null;
  questions: AdvisorProgressQuestion[];
}

export interface AdvisorProgress {
  workId: string;
  title: string;
  status: string;
  summary: {
    questionCount: number;
    discussedCount: number;
    photoCount: number;
    /** 和叙叙聊的通数 */
    chatCount: number;
    /** 自己讲的段数（不挂话题） */
    selfRecordingCount: number;
    lastNarratedAt: number | null;
    lastPhotoAt: number | null;
    chapterCount: number;
  };
  topics: AdvisorProgressTopic[];
}

/** 题面上限，与 content-pipeline interview-outline.service 一致 */
export const QUESTION_TEXT_MAX_LENGTH = 100;
/** 改写意见上限，与 content-pipeline interview-outline-regenerate.service 一致 */
export const ADVICE_MAX_LENGTH = 300;

function workPath(customerId: string, workId: string): string {
  return `/advisor/customers/${encodeURIComponent(customerId)}/works/${encodeURIComponent(workId)}`;
}

// ── 销售代传录音 ──────────────────────────────────────────────────────────────
// 与用户在小程序里「上传已有录音」同一条状态机：上传 → 转码识别 → [采访：选讲述人] → 入管线。

export type AudioImportMode = 'solo' | 'interview';
export type AudioImportStatus = 'uploading' | 'transcribing' | 'awaiting_speaker' | 'ingesting' | 'done' | 'failed';

export interface AudioImportSpeakerDto {
  speakerId: string;
  label: string;
  /** 该说话人总发言时长（毫秒） */
  talkMs: number;
  /** 发言占比 0-100（整数） */
  sharePct: number;
  /** 最多 3 句、每句 ≤40 字，供辨认「哪位是讲述人」（客户讲述原话，仅详情接口返回） */
  sampleLines: string[];
}

export interface AudioImportDto {
  importId: string;
  fileName: string;
  mode: AudioImportMode;
  status: AudioImportStatus;
  durationMs: number | null;
  createdAt: number;
  speakers?: AudioImportSpeakerDto[];
  recordingId?: string;
  chapterHint?: string;
  /** 'sales' = 销售代传；没有 = 客户自己在小程序上传 */
  uploadedBy?: 'sales';
  error?: string;
  retryable?: boolean;
  /** diarization = 火山说话人分离（较准）；llm = 兜底按语义判断（可能不准，需核对） */
  attribution?: 'diarization' | 'llm';
}

function audioImportsPath(customerId: string, workId: string): string {
  return `${workPath(customerId, workId)}/audio-imports`;
}

/** 列表（轮询用；不含说话人样例句） */
export async function listAudioImports(customerId: string, workId: string) {
  const resp = await apiClient.get<ServiceResponse<AudioImportDto[]>>(audioImportsPath(customerId, workId));
  return resp.data.data;
}

/** 详情：待选讲述人时带说话人样例句；读它会留痕 */
export async function getAudioImport(customerId: string, workId: string, importId: string) {
  const resp = await apiClient.get<ServiceResponse<AudioImportDto>>(
    `${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}`,
  );
  return resp.data.data;
}

export async function createAudioImport(
  customerId: string,
  workId: string,
  body: { fileName: string; sizeBytes: number; mode: AudioImportMode },
) {
  const resp = await apiClient.post<ServiceResponse<{ importId: string; chunkSize: number; totalChunks: number }>>(
    audioImportsPath(customerId, workId),
    body,
  );
  return resp.data.data;
}

/** 一块原始字节（PUT，幂等；重复上传同一序号覆盖即可） */
export async function uploadAudioImportChunk(
  customerId: string,
  workId: string,
  importId: string,
  index: number,
  chunk: Blob,
  signal?: AbortSignal,
) {
  const resp = await apiClient.put<ServiceResponse<{ received: number[] }>>(
    `${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}/chunks/${index}`,
    chunk,
    { headers: { 'Content-Type': 'application/octet-stream' }, signal, timeout: 120_000 },
  );
  return resp.data.data;
}

export async function completeAudioImport(customerId: string, workId: string, importId: string) {
  const resp = await apiClient.post<ServiceResponse<AudioImportDto>>(
    `${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}/complete`,
    {},
  );
  return resp.data.data;
}

export async function chooseAudioImportNarrator(customerId: string, workId: string, importId: string, speakerId: string) {
  const resp = await apiClient.post<ServiceResponse<AudioImportDto>>(
    `${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}/narrator`,
    { speakerId },
  );
  return resp.data.data;
}

export async function retryAudioImport(customerId: string, workId: string, importId: string) {
  const resp = await apiClient.post<ServiceResponse<AudioImportDto>>(
    `${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}/retry`,
    {},
  );
  return resp.data.data;
}

export async function deleteAudioImport(customerId: string, workId: string, importId: string) {
  await apiClient.delete<ServiceResponse<null>>(`${audioImportsPath(customerId, workId)}/${encodeURIComponent(importId)}`);
}

export async function getAdvisorProgress(customerId: string, workId: string) {
  const resp = await apiClient.get<ServiceResponse<AdvisorProgress>>(`${workPath(customerId, workId)}/progress`);
  return resp.data.data;
}

export async function updateAdvisorQuestion(customerId: string, workId: string, questionId: string, text: string) {
  await apiClient.patch<ServiceResponse<null>>(
    `${workPath(customerId, workId)}/outline/questions/${encodeURIComponent(questionId)}`,
    { text },
  );
}

export async function deleteAdvisorQuestion(customerId: string, workId: string, questionId: string) {
  await apiClient.delete<ServiceResponse<null>>(
    `${workPath(customerId, workId)}/outline/questions/${encodeURIComponent(questionId)}`,
  );
}

export async function addAdvisorQuestion(customerId: string, workId: string, topicId: string, text: string) {
  const resp = await apiClient.post<ServiceResponse<{ questionId: string }>>(
    `${workPath(customerId, workId)}/outline/topics/${encodeURIComponent(topicId)}/questions`,
    { text },
  );
  return resp.data.data;
}

/** 按意见改写要等一次大模型，超时放宽到服务端（120s）之上 */
export async function regenerateAdvisorOutline(customerId: string, workId: string, advice: string) {
  await apiClient.post<ServiceResponse<unknown>>(
    `${workPath(customerId, workId)}/outline/regenerate`,
    { advice },
    { timeout: 130_000 },
  );
}

// ── 话题增删改与按背景生成（P2-2）────────────────────────────────────────────

/** 话题名上限，与 content-pipeline interview-outline.service 一致 */
export const TOPIC_TITLE_MAX_LENGTH = 20;
/** 新话题一次最多带多少道题，与 content-pipeline 一致 */
export const MAX_QUESTIONS_PER_NEW_TOPIC = 8;

export async function addAdvisorTopic(
  customerId: string,
  workId: string,
  input: { title: string; afterTopicId?: string; questions?: string[] },
) {
  const resp = await apiClient.post<ServiceResponse<{ topicId: string }>>(
    `${workPath(customerId, workId)}/outline/topics`,
    input,
  );
  return resp.data.data;
}

export async function renameAdvisorTopic(customerId: string, workId: string, topicId: string, title: string) {
  await apiClient.patch<ServiceResponse<null>>(
    `${workPath(customerId, workId)}/outline/topics/${encodeURIComponent(topicId)}`,
    { title },
  );
}

export async function deleteAdvisorTopic(customerId: string, workId: string, topicId: string) {
  await apiClient.delete<ServiceResponse<null>>(
    `${workPath(customerId, workId)}/outline/topics/${encodeURIComponent(topicId)}`,
  );
}

/** 大纲方案里的一条改动（与 content-pipeline advisor-outline-propose.service 的 OutlineOp 一致） */
export type OutlineOp =
  | { kind: 'remove_topic'; topicId: string; reason: string }
  | { kind: 'add_topic'; title: string; afterTopicId: string | null; questions: string[]; reason: string }
  | { kind: 'rewrite_question'; questionId: string; text: string; reason: string }
  | { kind: 'remove_question'; questionId: string; reason: string }
  | { kind: 'add_question'; topicId: string; text: string; reason: string };

export interface ProposedChange {
  id: string;
  op: OutlineOp;
  /** 改动涉及的话题名（删话题 / 在哪个话题下补题 / 新话题放在哪个话题后面 / 被改题所在话题） */
  topicTitle?: string;
  /** 被改写 / 被删的题的原题面 */
  currentText?: string;
}

export interface OutlineProposal {
  summary: string;
  changes: ProposedChange[];
  /** 模型给出但不符合规则被丢弃的条数 */
  dropped: number;
}

export interface ApplyOutlineResult {
  applied: number;
  skipped: Array<{ index: number; kind: string; reason: string }>;
}

/** 补充意见上限，与 content-pipeline 一致 */
export const PROPOSE_ADVICE_MAX_LENGTH = 300;

/** 按这本书的背景生成方案：**不落库**，要等一次大模型，超时放宽到服务端（120s）之上 */
export async function proposeAdvisorOutline(customerId: string, workId: string, advice: string) {
  const resp = await apiClient.post<ServiceResponse<OutlineProposal>>(
    `${workPath(customerId, workId)}/outline/propose`,
    { advice },
    { timeout: 130_000 },
  );
  return resp.data.data;
}

/** 应用销售勾选的改动；服务端逐条按当前大纲重新校验，违规的进 skipped */
export async function applyAdvisorOutline(customerId: string, workId: string, ops: OutlineOp[]) {
  const resp = await apiClient.post<ServiceResponse<ApplyOutlineResult>>(
    `${workPath(customerId, workId)}/outline/apply`,
    { ops },
    { timeout: 130_000 },
  );
  return resp.data.data;
}

// ── 销售下载 PDF（PR⑤b）──────────────────────────────────────────────────────

export interface AdvisorPdfStatus {
  latest: { pdfId: string; type: 'preview' | 'final' | string; pageCount: number | null; createdAt: number } | null;
  generating: boolean;
}

export async function getAdvisorPdfStatus(workId: string) {
  const resp = await apiClient.get<ServiceResponse<AdvisorPdfStatus>>(`${w(workId)}/pdf`);
  return resp.data.data;
}

export async function startAdvisorPdf(workId: string) {
  const resp = await apiClient.post<ServiceResponse<AdvisorPdfStatus>>(`${w(workId)}/pdf`, {});
  return resp.data.data;
}

/**
 * 下载 PDF：带登录态取字节、在浏览器本地存成文件。
 * 不用 <a href> 直链——后台接口要鉴权，也不存在可以外传的文件地址。
 */
export async function downloadAdvisorPdf(workId: string, pdfId: string, fallbackName: string): Promise<void> {
  let resp;
  try {
    resp = await apiClient.get<Blob>(`${w(workId)}/pdfs/${encodeURIComponent(pdfId)}/download`, {
      responseType: 'blob',
      timeout: 120_000,
    });
  } catch (e: any) {
    // 失败体被当成 Blob 收下了：读出 JSON 里的提示，交给调用方展示
    const blob = e?.response?.data;
    if (blob instanceof Blob) {
      try {
        const body = JSON.parse(await blob.text());
        e.response.data = body;
      } catch {
        /* 非 JSON，保留原样 */
      }
    }
    throw e;
  }
  const disposition = String(resp.headers['content-disposition'] ?? '');
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(resp.data);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

// ── 企微聊天侧边栏 + 备用通道（PR⑦）──────────────────────────────────────────

export type AdvisorCardType = 'order' | 'topic' | 'reading' | 'finalize';

/** 话题卡在「今日话题」里的推荐信息（P2-3）；不在今日推荐里的话题卡没有这一项 */
export interface CardRecommendationDto {
  /** 在今日推荐里的次序，从 1 起 */
  rank: number;
  /** continue = 接着聊（已聊一部分）；next = 还没聊过的下一个 */
  kind: 'continue' | 'next';
  reason: string;
  /** 今天已经发过 */
  sentToday: boolean;
}

export interface AdvisorCardDto {
  key: string;
  type: AdvisorCardType;
  /** 客户看到的卡片标题（默认值，发送前可改） */
  title: string;
  /** 给销售看的说明 */
  subtitle: string;
  /** 小程序页面路径（不带 .html） */
  page: string;
  query: string;
  imgUrl: string;
  /** 这张卡属于哪本书（讲述人），给销售区分爸妈两本书；不发给客户 */
  bookLabel: string;
  /** 阅读 / 定稿 / 话题卡所属的书；话题卡另带话题 */
  workId?: string;
  topicId?: string;
  /** 今日推荐（P2-3）：只有在今日推荐里的话题卡才有 */
  recommendation?: CardRecommendationDto;
  /** 这个话题的卡片最近一次被发出的时间（推送记录）；没发过为 null */
  lastSentAt?: number | null;
}

/** 一本书的「今日话题」计划（P2-3）：今天建议发几个、距交付几天、客户几天没开口 */
export interface TodayPlanDto {
  workId: string;
  bookLabel: string;
  /** 今天建议发几个话题（含今天已经发出的） */
  quota: number;
  remainingQuestions: number;
  remainingTopics: number;
  /** 期望交付日期（YYYY-MM-DD）；没填为 null */
  deadline: string | null;
  /** 距期望交付还有几天（含今天）；没填或已过为 null */
  daysLeft: number | null;
  /** 已经超过期望交付日几天；没过为 null */
  overdueDays: number | null;
  /** 客户已经几天没讲了；从没讲过为 null */
  idleDays: number | null;
  /** 今天已经发出的话题数 */
  sentToday: number;
}

export type WecomContextDto =
  | {
      status: 'bound';
      customer: AdvisorCustomerSummaryDto;
      miniprogramAppId: string;
      cards: AdvisorCardDto[];
      plans: TodayPlanDto[];
    }
  | { status: 'taken' }
  | {
      status: 'unbound';
      candidates: Array<{
        customerId: string;
        displayName: string;
        narrators: BookNarratorDto[];
        createdAt: number;
      }>;
    };

export async function getWecomContext(externalUserId: string) {
  const resp = await apiClient.get<ServiceResponse<WecomContextDto>>('/advisor/wecom/context', {
    params: { externalUserId },
  });
  return resp.data.data;
}

export async function bindWecomContact(customerId: string, externalUserId: string) {
  const resp = await apiClient.post<ServiceResponse<{ customerId: string }>>(
    `/advisor/customers/${encodeURIComponent(customerId)}/wecom-bind`,
    { externalUserId },
  );
  return resp.data.data;
}

export async function listAdvisorCards(customerId: string) {
  const resp = await apiClient.get<ServiceResponse<{ miniprogramAppId: string; cards: AdvisorCardDto[]; plans: TodayPlanDto[] }>>(
    `/advisor/customers/${encodeURIComponent(customerId)}/cards`,
  );
  return resp.data.data;
}

export async function generateAdvisorCardLink(customerId: string, cardKey: string) {
  const resp = await apiClient.post<ServiceResponse<{ shortLink: string; qrcodePngBase64: string }>>(
    `/advisor/customers/${encodeURIComponent(customerId)}/cards/link`,
    { cardKey },
  );
  return resp.data.data;
}

export async function reportAdvisorCardSent(customerId: string, cardKey: string) {
  await apiClient.post(`/advisor/customers/${encodeURIComponent(customerId)}/cards/sent`, { cardKey });
}

// ── A-5 交叉用户（PR⑧，仅超管）────────────────────────────────────────────────

export type OverlapStatusFilter = 'on_hold' | 'resolved' | 'all';

export interface AdvisorOverlapOrderDto {
  advisorOrderId: string;
  customerId: string;
  customerName: string;
  narratorName: string | null;
  advisorNickname: string | null;
  detectedAt: number | null;
  amountFen: number;
  orderStatus: string;
  paidAt: number | null;
  workId: string | null;
  partner: { partnerId: string | null; name: string | null; city: string | null; status: string | null };
  /** on_hold 待判定 / released 已发给合伙人 / waived 不发 / void 已退款作废 */
  holdStatus: string;
  holdFen: number | null;
  resolvedAt: number | null;
  resolvedBy: string | null;
  note: string | null;
  commissionEntryId: string | null;
  entryStatus: string | null;
  printCommitted: boolean;
}

export interface AdvisorOverlapFlagOnlyDto {
  customerId: string;
  customerName: string;
  advisorNickname: string | null;
  partnerId: string;
  partnerName: string | null;
  detectedAt: number | null;
}

export async function listAdvisorOverlaps(status: OverlapStatusFilter) {
  const resp = await apiClient.get<
    ServiceResponse<{ orders: AdvisorOverlapOrderDto[]; flagOnly: AdvisorOverlapFlagOnlyDto[] }>
  >('/advisor/overlaps', { params: { status } });
  return resp.data.data;
}

export async function resolveAdvisorCommissionHold(
  advisorOrderId: string,
  input: { decision: 'release' | 'waive'; note?: string },
) {
  const resp = await apiClient.post<
    ServiceResponse<{ commissionHoldStatus: string; commissionEntryId: string | null; entryStatus: string | null }>
  >(`/advisor/orders/${encodeURIComponent(advisorOrderId)}/commission-hold/resolve`, input);
  return resp.data.data;
}
